import React, { useState, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';

interface BatchAnalysisUploaderProps {
  session: Session;
  onBatchCreated?: (batchJobId: string) => void;
}

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'failed';
  progress: number;
  error?: string;
}

export const BatchAnalysisUploader: React.FC<BatchAnalysisUploaderProps> = ({ 
  session, 
  onBatchCreated 
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [batchName, setBatchName] = useState('');
  const [batchDescription, setBatchDescription] = useState('');
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: UploadFile[] = Array.from(selectedFiles).map((file, index) => ({
      file,
      id: `${Date.now()}-${index}`,
      status: 'pending' as const,
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  // Remove file from list
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Upload single file to storage
  const uploadFile = async (uploadFile: UploadFile): Promise<string | null> => {
    try {
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'uploading' as const, progress: 0 } : f
      ));

      const fileName = `${session.user.id}/${Date.now()}-${uploadFile.file.name}`;
      
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(fileName, uploadFile.file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(data.path);

      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'uploaded' as const, progress: 100 } : f
      ));

      return publicUrl;
    } catch (error) {
      setFiles(prev => prev.map(f => 
        f.id === uploadFile.id ? { 
          ...f, 
          status: 'failed' as const, 
          error: error instanceof Error ? error.message : 'Upload failed' 
        } : f
      ));
      return null;
    }
  };

  // Create batch analysis job
  const handleCreateBatch = async () => {
    if (files.length === 0) {
      alert('Bitte mindestens eine Datei hochladen');
      return;
    }

    if (!batchName.trim()) {
      alert('Bitte einen Namen für die Stapelanalyse eingeben');
      return;
    }

    setIsCreatingBatch(true);

    try {
      // 1. Create batch job
      const { data: batchJob, error: batchError } = await supabase
        .from('batch_analysis_jobs')
        .insert({
          user_id: session.user.id,
          name: batchName,
          description: batchDescription,
          total_documents: files.length,
          status: 'pending'
        })
        .select()
        .single();

      if (batchError) throw batchError;

      // 2. Upload all files
      const uploadPromises = files.map(uploadFile);
      const uploadedUrls = await Promise.all(uploadPromises);

      // 3. Create batch document records
      const batchDocuments = files.map((file, index) => ({
        batch_job_id: batchJob.id,
        file_name: file.file.name,
        file_url: uploadedUrls[index],
        file_size: file.file.size,
        processing_order: index,
        status: uploadedUrls[index] ? 'queued' : 'failed'
      })).filter(doc => doc.file_url);

      const { error: docsError } = await supabase
        .from('batch_analysis_documents')
        .insert(batchDocuments);

      if (docsError) throw docsError;

      // 4. Trigger batch processing
      const { error: functionError } = await supabase.functions.invoke('process-batch-analysis', {
        body: { batchJobId: batchJob.id }
      });

      if (functionError) {
        console.error('Failed to start batch processing:', functionError);
      }

      // Success - notify parent
      if (onBatchCreated) {
        onBatchCreated(batchJob.id);
      }

      // Reset form
      setFiles([]);
      setBatchName('');
      setBatchDescription('');
      
      alert(`Stapelanalyse "${batchName}" wurde erfolgreich erstellt! Die Verarbeitung läuft im Hintergrund.`);
    } catch (error) {
      console.error('Error creating batch:', error);
      alert('Fehler beim Erstellen der Stapelanalyse: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Stapel-Dokumentenanalyse
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Laden Sie mehrere Dokumente gleichzeitig hoch für automatische Analyse und Vergleich
        </p>
      </div>

      {/* Batch Info */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Name der Stapelanalyse *
          </label>
          <input
            type="text"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="z.B. Mietverträge Q4 2024"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-luna-secondary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung (optional)
          </label>
          <textarea
            value={batchDescription}
            onChange={(e) => setBatchDescription(e.target.value)}
            placeholder="Zusätzliche Informationen zur Stapelanalyse..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-luna-secondary focus:border-transparent resize-none"
          />
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-luna-secondary bg-luna-secondary/10'
            : 'border-gray-300 dark:border-gray-600 hover:border-luna-secondary/50'
        }`}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <span className="material-symbols-outlined text-5xl text-gray-400 mb-4">
          cloud_upload
        </span>
        
        <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
          Dateien hier ablegen oder klicken zum Auswählen
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Unterstützt: PDF, DOC, DOCX, TXT • Bis zu 100 Dateien
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Hochgeladene Dateien ({files.length})
            </h3>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Gesamt: {formatBytes(totalSize)}
            </span>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="material-symbols-outlined text-gray-400">
                    {file.status === 'uploaded' ? 'check_circle' : 
                     file.status === 'failed' ? 'error' :
                     file.status === 'uploading' ? 'sync' : 'description'}
                  </span>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatBytes(file.file.size)}
                      {file.status === 'uploading' && ` • ${file.progress}%`}
                      {file.error && ` • ${file.error}`}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => removeFile(file.id)}
                  className="ml-3 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  disabled={file.status === 'uploading'}
                >
                  <span className="material-symbols-outlined text-gray-400 text-xl">close</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreateBatch}
          disabled={files.length === 0 || !batchName.trim() || isCreatingBatch}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-luna-primary to-luna-secondary text-white font-semibold rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isCreatingBatch ? (
            <span className="flex items-center justify-center gap-2">
              <span className="material-symbols-outlined animate-spin">sync</span>
              Stapelanalyse wird erstellt...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <span className="material-symbols-outlined">play_arrow</span>
              Stapelanalyse starten
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setFiles([]);
            setBatchName('');
            setBatchDescription('');
          }}
          disabled={isCreatingBatch}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Zurücksetzen
        </button>
      </div>
    </div>
  );
};
